import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Directions as DirectionsIcon,
  Language as LanguageIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { formatWorkingHoursCompact } from "../../../../../utils/shopUtils";

export interface ShopDetailsDialogData {
  type: "trusted" | "google";
  name: string;
  address?: string | null;
  phoneNumber?: string | null;
  workingHours?: string | null;
  description?: string | null;
  website?: string | null;
  rating?: number | null;
  userRatingsTotal?: number | null;
}

interface ShopDetailsDialogProps {
  open: boolean;
  details: ShopDetailsDialogData | null;
  onClose: () => void;
}

interface DetailRowProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ icon, label, children }) => (
  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
    <Box sx={{ color: "text.secondary", display: "flex", pt: 0.25 }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
      >
        {children}
      </Typography>
    </Box>
  </Box>
);

export const ShopDetailsDialog: React.FC<ShopDetailsDialogProps> = ({
  open,
  details,
  onClose,
}) => {
  if (!details) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1700 }}
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
        },
      }}
    >
      <DialogTitle
        sx={{
          pr: 6,
          fontSize: "0.875rem",
          fontWeight: 600,
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
        }}
      >
        <Typography variant="subtitle1" component="div" fontWeight={600}>
          {details.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {details.type === "trusted" ? "Trusted shop" : "Google result"}
        </Typography>
        <IconButton
          aria-label="Close shop details"
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          {details.address && (
            <DetailRow
              icon={<DirectionsIcon fontSize="small" />}
              label="Address"
            >
              {details.address}
            </DetailRow>
          )}

          {details.phoneNumber && (
            <DetailRow icon={<PhoneIcon fontSize="small" />} label="Phone">
              <Link href={`tel:${details.phoneNumber}`} underline="hover">
                {details.phoneNumber}
              </Link>
            </DetailRow>
          )}

          {details.workingHours && (
            <DetailRow
              icon={<AccessTimeIcon fontSize="small" />}
              label="Working hours"
            >
              {formatWorkingHoursCompact(details.workingHours)}
            </DetailRow>
          )}

          {details.website && (
            <DetailRow icon={<LanguageIcon fontSize="small" />} label="Website">
              <Link href={details.website} target="_blank" rel="noreferrer">
                {details.website}
              </Link>
            </DetailRow>
          )}

          {details.description && (
            <DetailRow
              icon={<DescriptionIcon fontSize="small" />}
              label="Description"
            >
              {details.description}
            </DetailRow>
          )}

          {details.rating && (
            <DetailRow icon={<StarIcon fontSize="small" />} label="Rating">
              {details.rating.toFixed(1)}
              {details.userRatingsTotal
                ? ` (${details.userRatingsTotal} reviews)`
                : ""}
            </DetailRow>
          )}

          {!details.address &&
            !details.phoneNumber &&
            !details.workingHours &&
            !details.website &&
            !details.description &&
            !details.rating && (
              <Typography variant="body2" color="text.secondary">
                No additional details available for this shop.
              </Typography>
            )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
